import React from 'react';
import { Search, X } from 'lucide-react';
import { useTheme, DEFAULT_THEME } from '../contexts/ThemeContext';

/**
 * SearchInput — barra de búsqueda reutilizable.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * POR QUÉ ESTE MARKUP (patrón A: flex + inset-y-0) Y NO `top-1/2 -translate-y-1/2`
 * ─────────────────────────────────────────────────────────────────────────────
 * El proyecto usa Tailwind v2.2.19 servido por CDN (ver public/index.html), sin
 * build ni JIT. En v2 las utilidades `translate-*`, `scale-*` y `rotate-*` NO
 * emiten la propiedad `transform`: sólo setean variables CSS.
 *
 *   .-translate-y-1\/2 { --tw-translate-y: -50%; }   <-- sólo la variable
 *   .transform         { transform: translateX(var(--tw-translate-x))
 *                                   translateY(var(--tw-translate-y)) ... }
 *
 * Es decir, `transform:` la emite ÚNICAMENTE la clase `.transform`. Si escribís
 * `absolute left-3 top-1/2 -translate-y-1/2` SIN añadir `transform`, la variable
 * queda seteada pero nadie la consume: el icono NO se recentra y queda anclado
 * al 50% del alto, desplazado media altura hacia abajo.
 *
 * El patrón A esquiva el problema por completo: un contenedor absoluto que
 * ocupa todo el alto (`inset-y-0`) y centra el icono con flexbox
 * (`flex items-center`). Cero dependencia de `transform`, así que es inmune al
 * bug y seguiría funcionando igual si algún día se migra a Tailwind v3/v4.
 *
 * `pointer-events-none` en el contenedor del icono deja que el click atraviese
 * y enfoque el input.
 *
 * NOTA: el padding-left del input (`pl-8`/`pl-10`) es lo que reserva el hueco
 * para la lupa. Hasta este fix, un bloque global `input[type="text"]` en
 * pages/ClientManagement.css (especificidad 0,1,1 > 0,1,0 de `.pl-10`) lo
 * pisaba y el texto se solapaba sobre el icono. Ese bloque ya está acotado.
 */

const SIZES = {
  sm: {
    input: 'pl-8 py-1.5 text-xs',
    padRight: { clear: 'pr-7', plain: 'pr-3' },
    iconBox: 'pl-2.5',
    clearBox: 'pr-2',
    icon: 12,
    clearIcon: 12,
  },
  md: {
    input: 'pl-10 py-2 text-sm',
    padRight: { clear: 'pr-9', plain: 'pr-4' },
    iconBox: 'pl-3',
    clearBox: 'pr-3',
    icon: 16,
    clearIcon: 14,
  },
  lg: {
    input: 'pl-10 py-3',
    padRight: { clear: 'pr-10', plain: 'pr-4' },
    iconBox: 'pl-3',
    clearBox: 'pr-3',
    icon: 18,
    clearIcon: 16,
  },
};

function SearchInput({
  value,
  onChange,
  placeholder = 'Buscar...',
  onClear,
  className = '',
  inputClassName = '',
  // Borde y radio van como props y NO por `inputClassName`: si el mismo
  // elemento lleva dos utilidades del mismo grupo (p.ej. `border` y `border-2`,
  // o `rounded-lg` y `rounded-xl`) gana la que se declare después EN EL CSS
  // generado, no la última del atributo class. Sustituyéndolas evitamos esa
  // lotería y el override es determinista.
  borderClass = 'border border-gray-300',
  roundedClass = 'rounded-lg',
  blurColor = '#d1d5db',
  size = 'md',
  disabled = false,
  autoFocus = false,
  ...rest
}) {
  // Fallback defensivo: si el componente se monta fuera del ThemeProvider,
  // el contexto trae DEFAULT_THEME plano (sin la clave `theme`).
  const ctx = useTheme();
  const focusColor =
    (ctx && ctx.theme && ctx.theme.primary) || DEFAULT_THEME.primary;
  const s = SIZES[size] || SIZES.md;

  const showClear = typeof onClear === 'function' && !!value && !disabled;
  const paddingRight = showClear ? s.padRight.clear : s.padRight.plain;

  return (
    <div className={`relative ${className}`}>
      {/* Lupa: contenedor a alto completo + flex (patrón A, sin transform) */}
      <div
        className={`absolute inset-y-0 left-0 ${s.iconBox} flex items-center pointer-events-none`}
      >
        <Search size={s.icon} className="text-gray-400" />
      </div>

      <input
        type="text"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        /* El estado disabled se resuelve con un ternario y NO con el variante
           `disabled:` de Tailwind: en el build estático de v2 ese variante no
           viene habilitado para background/cursor, así que sería inerte. */
        className={`w-full ${s.input} ${paddingRight} ${borderClass} ${roundedClass} text-gray-700 placeholder-gray-400 focus:outline-none transition-colors ${
          disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
        } ${inputClassName}`}
        onFocus={(e) => (e.target.style.borderColor = focusColor)}
        onBlur={(e) => (e.target.style.borderColor = blurColor)}
        {...rest}
      />

      {/* Botón limpiar: mismo patrón A, alineado a la derecha */}
      {showClear && (
        <button
          type="button"
          onClick={onClear}
          aria-label="Limpiar búsqueda"
          className={`absolute inset-y-0 right-0 ${s.clearBox} flex items-center text-gray-400 hover:text-gray-600 focus:outline-none`}
        >
          <X size={s.clearIcon} />
        </button>
      )}
    </div>
  );
}

export default SearchInput;
